import { differenceInBusinessDays, subMonths } from "date-fns";

import type { TranslationKey } from "@shared/i18n/translationKey";
import type { DaysAvailable, LicenseApplication } from "../api/licenseApplicationApi";

/**
 * Veredicto de la validacion de dias pedidos. No es una union discriminada a proposito:
 * los tests existentes leen `messageKey` sin estrechar por `valid`, y esos tests no se
 * tocan en la migracion.
 */
export interface RequestedDaysVerdict {
  valid: boolean;
  titleKey?: TranslationKey;
  messageKey?: TranslationKey;
  params?: Record<string, unknown>;
}

/**
 * Lo que `findActivePendingRequest` realmente lee de cada solicitud. Mas laxo que
 * LicenseApplication: los fixtures de los tests no traen `supervisorEmail`, que el DTO
 * declara obligatorio, y esos tests quedan intactos.
 */
export type PendingRequestLike = Partial<LicenseApplication>;

import {
  ACTIVE_STATUSES,
  ALLOWED_FILE_EXTENSIONS,
  DAYS_ALLOWED,
  LEAVE_TYPE,
  MAX_FILE_SIZE_BYTES,
  TYPES_FROM_AVAILABILITY,
  TYPES_UNPAID,
} from "../constants";

// Reglas de negocio puras del módulo license-application.

/** Días hábiles que abarca el rango [start, end], ambos inclusive. */
export const businessDaysBetween = (startDate: Date, endDate: Date) =>
  differenceInBusinessDays(endDate, startDate) + 1;

/** Nunca mostramos días negativos en los contadores de disponibilidad. */
export const clampDays = (days: number) => (days < 0 ? 0 : days);

/** La extensión del archivo adjunto está dentro de la lista permitida. */
export const isAllowedFileType = (fileName?: string, extensions: readonly string[] = ALLOWED_FILE_EXTENSIONS) => {
  const name = (fileName ?? "").toLowerCase();
  return extensions.some((ext) => name.endsWith(`.${ext}`));
};

/** El archivo adjunto no supera el tamaño máximo (el tope exacto se acepta). */
export const isWithinFileSize = (size: number, max: number = MAX_FILE_SIZE_BYTES) => size <= max;

/**
 * Fecha mínima seleccionable en el formulario: las constancias de enfermedad
 * admiten hasta un mes hacia atrás; el resto, solo desde hoy.
 */
export const proofIllnessMinDate = (type?: string, now: Date = new Date()) =>
  type === LEAVE_TYPE.SICKNESS_CERTIFICATE ? subMonths(now, 1) : now;

/**
 * Devuelve la solicitud del mismo tipo que bloquea una nueva (o null). Bloquea
 * cuando está vigente: no aprobada/rechazada, en un estado activo del workflow
 * y dentro del año en curso.
 *
 * @param {Array|undefined} requestList - solicitudes existentes del colaborador.
 * @param {string} type - tipo de solicitud que se intenta registrar.
 * @param {Date} [now] - fecha de referencia (inyectable para testear).
 */
export const findActivePendingRequest = (requestList: PendingRequestLike[] = [], type?: string, now: Date = new Date()) => {
  if (!requestList) return null;
  const match = requestList.find((element) => element.type === type);
  if (!match) return null;
  const sameYear = new Date(match.startDate ?? "").getFullYear() === now.getFullYear();
  const isActive =
    !match.approved && !match.notApproved && ACTIVE_STATUSES.includes(match.status ?? "");
  return isActive && sameYear ? match : null;
};

/**
 * Evalúa si la cantidad de días hábiles solicitada respeta los topes según el
 * tipo de solicitud. Devuelve `{ valid: true }` o, si excede algún tope,
 * `{ valid: false, titleKey, messageKey, params }` con las claves i18n y los
 * parámetros que el componente debe mostrar en el InfoAlert.
 */
export const evaluateRequestedDays = ({ type, days, vacationDays }: { type: string; days: number; vacationDays: DaysAvailable }): RequestedDaysVerdict => {
  const invalid = (titleKey: TranslationKey, messageKey: TranslationKey, params: Record<string, unknown>): RequestedDaysVerdict =>
    ({ valid: false, titleKey, messageKey, params });

  if (TYPES_FROM_AVAILABILITY.includes(type)) {
    if (days > (vacationDays.daysAvailability ?? 0)) {
      return invalid("requestedDays", "cannotBeGreaterThanVailableDays", {
        daysAvailability: vacationDays.daysAvailability,
      });
    }
    if (days > DAYS_ALLOWED.PAID) {
      return invalid("requestedDays", "onlyOneDayIsAllowedForApplications", {
        daysPaid: DAYS_ALLOWED.PAID,
        type,
      });
    }
  } else if (type === LEAVE_TYPE.SICKNESS_CERTIFICATE) {
    if (days > (vacationDays.daysSicknessRecord ?? 0)) {
      return invalid("daysForProofIllnessRequested", "cannotBeGreaterThanVailableDays", {
        daysAvailability: vacationDays.daysSicknessRecord,
      });
    }
  } else if (TYPES_UNPAID.includes(type)) {
    if (days > DAYS_ALLOWED.NOT_PAID) {
      return invalid("requestedUnpaidDays", "cannotBeLongerAllowedDays", {
        daysNotPaid: DAYS_ALLOWED.NOT_PAID,
      });
    }
  }
  return { valid: true };
};
